import React from 'react';
import { FixMeLater } from '../../utils';
import { useStyles } from './Score.styles';
import {
    ResponsiveStyleValue,
    ScoreColor,
    ScoreSize,
    useComponentDefaultProps,
    useHoneycombTheme,
} from '../../theming';
import { Typography, TypographyBaseProps } from '../Typography/Typography';

export interface ScoreProps extends Omit<TypographyBaseProps, 'color'> {
    /**
     * The colour of the score.
     * @default neutral
     */
    color?: ScoreColor;

    /**
     * The size of the score.
     * Use object syntax to provide responsive values. The value will apply to the given breakpoint and up, unless overridden.
     * @default m
     */
    size?: ResponsiveStyleValue<ScoreSize>;
}

const defaultProps: Partial<ScoreProps> = {
    color: 'neutral',
};

/**
 * `Score` is an themeable typography component. It is intended to display a numeric score or rating and can be adapted to suit branding requirements.
 */

export const Score: React.ForwardRefExoticComponent<ScoreProps & React.RefAttributes<HTMLElement>> = React.forwardRef(
    function Score(props: ScoreProps, ref: React.ForwardedRef<FixMeLater>): React.JSX.Element {
        const { color = 'neutral', size = 'm', ...other } = useComponentDefaultProps('Score', defaultProps, props);

        const { styles } = useStyles({ color, size });

        const { theme } = useHoneycombTheme();
        const { components } = theme;
        const componentTheme = components.themes.Score;

        return (
            <Typography
                ref={ref}
                css={styles.root}
                family={componentTheme.typographyFamily}
                weight={componentTheme.fontWeight}
                {...other}
            />
        );
    }
);
